import React from 'react';
import { Feather, Footprints, Mountain, Star, Target } from 'lucide-react';
import { SPORT_SUBCATEGORIES } from '../../utils/eventCategories';
import { TAIWAN_CITIES } from '../../utils/taiwanCities';
import { SECTION_THEME } from '../../utils/sectionTheme';

const FEATURED_SPORTS = [
  { value: '羽球', label: '羽球', icon: Feather },
  { value: '慢跑', label: '慢跑', icon: Footprints },
  { value: '爬山', label: '登山', icon: Mountain },
  { value: '匹克球', label: '匹克球', icon: Target }
];

const chipStyle = (isSelected) => ({
  minHeight: '44px',
  padding: '7px 12px',
  color: isSelected ? '#ffffff' : '#526579',
  background: isSelected ? SECTION_THEME.sports.accent : '#f5f9fc',
  border: isSelected ? `1px solid ${SECTION_THEME.sports.accent}` : '1px solid #dbe4ee',
  borderRadius: '999px',
  fontFamily: 'inherit',
  fontSize: '12px',
  fontWeight: '750',
  cursor: 'pointer'
});

const fieldStyle = {
  boxSizing: 'border-box',
  width: '100%',
  minHeight: '44px',
  padding: '9px 11px',
  color: '#334155',
  background: '#f5f9fc',
  border: '1px solid #dbe4ee',
  borderRadius: '12px',
  fontFamily: 'inherit',
  fontSize: '13px'
};

const sectionLabelStyle = {
  marginBottom: '8px',
  color: '#526579',
  fontSize: '12px',
  fontWeight: '750'
};

export default function SportFilterPanel({
  selectedSubcategory,
  setSelectedSubcategory,
  selectedCities,
  setSelectedCities,
  dateStartFilter,
  setDateStartFilter,
  dateEndFilter,
  setDateEndFilter,
  filterType,
  setFilterType,
  activeFilterCount,
  searchQuery,
  onClear,
  isMobile,
  variant = 'inline'
}) {
  const isDrawer = variant === 'drawer';
  const toggleCity = (city) => {
    setSelectedCities(previous => previous.includes(city)
      ? previous.filter(item => item !== city)
      : [...previous, city]);
  };
  const otherSports = SPORT_SUBCATEGORIES.filter(sport => !FEATURED_SPORTS.some(featured => featured.value === sport));

  return (
    <div data-filter-panel="sports" data-variant={variant} style={{ marginBottom: isDrawer ? 0 : '18px', padding: isDrawer ? 0 : '15px', background: isDrawer ? 'transparent' : 'rgba(255,255,255,0.94)', border: isDrawer ? 'none' : '1px solid #dbe4ee', borderRadius: isDrawer ? 0 : '18px', boxShadow: isDrawer ? 'none' : '0 10px 28px rgba(30,64,99,0.08)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: isDrawer ? 'flex-end' : 'space-between', gap: '12px', marginBottom: '13px' }}>
        {!isDrawer && <span style={{ color: '#334155', fontSize: '13px', fontWeight: '850' }}>運動篩選</span>}
        {(activeFilterCount > 0 || searchQuery) && <button type="button" onClick={onClear} style={{ minHeight: '44px', padding: '6px 8px', color: SECTION_THEME.sports.accent, background: 'transparent', border: 'none', fontFamily: 'inherit', fontSize: '12px', fontWeight: '800', cursor: 'pointer' }}>清除全部</button>}
      </div>

      <div style={{ display: 'grid', gap: '17px' }}>
        <div>
          <div style={sectionLabelStyle}>熱門運動</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '9px' }}>
            {FEATURED_SPORTS.map(option => {
              const Icon = option.icon;
              const isSelected = selectedSubcategory === option.value;
              return (
                <button key={option.value} type="button" aria-label={`篩選${option.label}`} onClick={() => setSelectedSubcategory(isSelected ? '' : option.value)} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '72px', padding: '9px 5px', color: isSelected ? '#ffffff' : SECTION_THEME.sports.accent, background: isSelected ? SECTION_THEME.sports.gradient : '#eef4f9', border: isSelected ? `1px solid ${SECTION_THEME.sports.accent}` : '1px solid #dbe4ee', borderRadius: '16px', boxShadow: isSelected ? '0 7px 16px rgba(78,139,202,0.2)' : 'none', cursor: 'pointer' }}>
                  <Icon size={21} />
                  <span style={{ marginTop: '5px', fontSize: '11px', fontWeight: '800' }}>{option.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <div style={sectionLabelStyle}>更多運動</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            <button type="button" onClick={() => setSelectedSubcategory('')} style={chipStyle(selectedSubcategory === '')}>全部運動</button>
            {otherSports.map(sport => <button key={sport} type="button" onClick={() => setSelectedSubcategory(selectedSubcategory === sport ? '' : sport)} style={chipStyle(selectedSubcategory === sport)}>{sport}</button>)}
          </div>
        </div>

        <div>
          <div style={sectionLabelStyle}>活動縣市</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', maxHeight: isMobile ? '118px' : 'none', overflowY: isMobile ? 'auto' : 'visible' }}>
            <button type="button" onClick={() => setSelectedCities([])} style={chipStyle(selectedCities.length === 0)}>全部縣市</button>
            {TAIWAN_CITIES.map(city => <button key={city} type="button" aria-pressed={selectedCities.includes(city)} onClick={() => toggleCity(city)} style={chipStyle(selectedCities.includes(city))}>{city}</button>)}
          </div>
        </div>

        <div>
          <div style={sectionLabelStyle}>運動日期區間</div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '10px' }}>
            <label htmlFor="sport-filter-start" style={{ display: 'grid', gap: '6px', color: '#526579', fontSize: '11px', fontWeight: '700' }}>開始日期<input id="sport-filter-start" type="date" aria-label="運動篩選開始日期" value={dateStartFilter} max={dateEndFilter || undefined} onChange={event => setDateStartFilter(event.target.value)} style={fieldStyle} /></label>
            <label htmlFor="sport-filter-end" style={{ display: 'grid', gap: '6px', color: '#526579', fontSize: '11px', fontWeight: '700' }}>結束日期<input id="sport-filter-end" type="date" aria-label="運動篩選結束日期" value={dateEndFilter} min={dateStartFilter || undefined} onChange={event => setDateEndFilter(event.target.value)} style={fieldStyle} /></label>
          </div>
        </div>

        <div>
          <div style={sectionLabelStyle}>更多條件</div>
          <button type="button" onClick={() => setFilterType(filterType === 'favorites' ? '' : 'favorites')} style={{ ...chipStyle(filterType === 'favorites'), display: 'inline-flex', alignItems: 'center', gap: '5px' }}><Star size={13} fill={filterType === 'favorites' ? 'currentColor' : 'none'} />我的收藏</button>
        </div>
      </div>
    </div>
  );
}
